import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import { Button, Divider, Paper, Stack, Tab, Tabs, Typography } from "@mui/material";

import { useOutletApi } from "@/features/authentication";
import { getErrorMessage } from "@/features/shared";
import {
  CreatePIEditableFieldsSection,
  CreatePIExpenseAccountsSection,
  CreatePIGeneralInformationSection,
  CreatePIProductVariantsSection,
  INCOMING_SALES_INVOICES_CHANGED_EVENT,
} from "@/features/sales-invoices";
import {
  DEFAULT_VALUES,
  VALIDATION_SCHEMA,
} from "@/features/sales-invoices/create-purchase-invoice-from-sales-invoice/create-purchase-invoice-form-constants";
import { DownPaymentAllocationsSection } from "@/features/sales-invoices/sales-invoice";
import {
  CenteredLoading,
  PageContainer,
  TabLabelWithCount,
  useSnackbar,
} from "@/features/ui";

export const CreatePurchaseInvoiceFromSalesInvoice = () => {
  const { salesInvoiceReferenceNumber } = useParams();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const api = useOutletApi();
  const [salesInvoice, setSalesInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);

  const methods = useForm({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });

  const { handleSubmit, formState, reset } = methods;

  // Seed one mapping row per source expense line once the invoice arrives. The
  // buyer picks its own account for each; amounts stay on the source invoice.
  useEffect(() => {
    if (!salesInvoice) return;
    reset((current) => ({
      ...current,
      expenseAccounts: (salesInvoice.salesInvoiceExpenses || []).map(
        (expense) => ({
          salesInvoiceExpenseId: expense.id,
          sellerAccountCode: expense.outletChartOfAccount?.code ?? null,
          sellerAccountName: expense.outletChartOfAccount?.name ?? null,
          amount: expense.amount,
          note: expense.note ?? null,
          outletChartOfAccount: null,
        })
      ),
    }));
  }, [salesInvoice, reset]);

  const loadSalesInvoice = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data },
      } = await api.get(
        // Buyer-scoped read: the invoice belongs to the seller outlet, so the
        // plain detail endpoint would 404 under our (buyer's) Outlet-Id.
        `sales-invoices/incoming/${salesInvoiceReferenceNumber}`,
        {
          params: {
            withBuyer: true,
            withSeller: true,
            withProductVariants: true,
            withPaymentTerm: true,
            withCreator: true,
            withSalesInvoiceExpenses: true,
            withSalesDownPaymentAllocations: true,
          },
        }
      );
      setSalesInvoice(data);
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memuat faktur penjualan"));
    } finally {
      setLoading(false);
    }
  }, [api, salesInvoiceReferenceNumber, showSnackbar]);

  useEffect(() => {
    loadSalesInvoice();
  }, [loadSalesInvoice]);

  const onSubmit = async (formData) => {
    try {
      const expenseAccounts = (formData.expenseAccounts || []).map((row) => ({
        salesInvoiceExpenseId: row.salesInvoiceExpenseId,
        outletChartOfAccountId: row.outletChartOfAccount.id,
      }));

      const payload = {
        transactionDate: dayjs(formData.transactionDate).format("YYYY-MM-DD"),
        sellerBankAccountId: formData.sellerBankAccount.id,
        note: formData.note || null,
        // Omitted entirely when the source invoice carries no expenses, matching
        // the discount_accounts contract on the purchase-payment conversion.
        ...(expenseAccounts.length > 0 && { expenseAccounts }),
      };

      await api.post(
        `sales-invoices/${salesInvoiceReferenceNumber}/purchase-invoices`,
        payload,
        {
          // The invoice's own buyer, not the ambient outlet.
          headers: {
            "Outlet-Id": salesInvoice.buyerableId,
          },
        }
      );
      window.dispatchEvent(new Event(INCOMING_SALES_INVOICES_CHANGED_EVENT));
      showSnackbar("Faktur pembelian berhasil dibuat", "success");
      navigate("/procurement/purchase-invoices");
    } catch (error) {
      // meta.error is either a field map (validation) or a plain string
      // (business rule, e.g. a missing outlet chart of account). Only the
      // field map has a friendlier wording; everything else is surfaced as-is
      // rather than swallowed.
      const metaError = error.response?.data?.meta?.error;
      if (metaError?.sellerReferenceNumber) {
        showSnackbar("No Faktur Penjual sudah ada");
      } else {
        showSnackbar(getErrorMessage(error, "Gagal membuat faktur pembelian"));
      }
      throw error;
    }
  };

  if (loading) {
    return <CenteredLoading sx={{ height: "100vh" }} />;
  }

  if (!salesInvoice) {
    return (
      <Typography variant="h6" color="error">
        Faktur penjualan tidak ditemukan
      </Typography>
    );
  }

  return (
    <PageContainer
      maxWidth="xl"
      title="Buat Faktur Pembelian dari Faktur Penjualan"
      breadcrumbs={[
        {
          title: "Faktur dari Penjual",
          path: "/procurement/incoming-sales-invoices",
        },
        { title: salesInvoiceReferenceNumber },
        { title: "Buat Faktur Pembelian" },
      ]}
    >
      <Stack spacing={2}>
        <CreatePIGeneralInformationSection salesInvoice={salesInvoice} />

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Stack spacing={2}>
              <CreatePIEditableFieldsSection
                sellerId={salesInvoice.seller?.id}
              />

              <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Tabs
                  value={tabIndex}
                  onChange={(_, newValue) => setTabIndex(newValue)}
                >
                  <Tab
                    label={
                      <TabLabelWithCount
                        label="Faktur"
                        count={
                          salesInvoice.salesInvoiceProductVariants?.length ?? 0
                        }
                      />
                    }
                  />
                  <Tab
                    label={
                      <TabLabelWithCount
                        label="Biaya Lain-lain"
                        count={salesInvoice.salesInvoiceExpenses?.length ?? 0}
                      />
                    }
                  />
                  <Tab
                    label={
                      <TabLabelWithCount
                        label="Uang Muka"
                        count={
                          salesInvoice.salesDownPaymentAllocations?.length ?? 0
                        }
                      />
                    }
                  />
                </Tabs>
                <Divider sx={{ mb: 2.5 }} />

                {tabIndex === 0 && (
                  <CreatePIProductVariantsSection
                    salesInvoiceProductVariants={
                      salesInvoice.salesInvoiceProductVariants
                    }
                  />
                )}
                {tabIndex === 1 && (
                  <CreatePIExpenseAccountsSection
                    buyerId={salesInvoice.buyerableId}
                  />
                )}
                {tabIndex === 2 && (
                  <Stack spacing={2}>
                    <DownPaymentAllocationsSection
                      salesDownPaymentAllocations={
                        salesInvoice.salesDownPaymentAllocations
                      }
                    />
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Uang muka tidak ikut disalin ke faktur pembelian.
                    </Typography>
                  </Stack>
                )}
              </Paper>

              <Stack direction="row" spacing={2} sx={{
                justifyContent: "flex-end"
              }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate(-1)}
                  disabled={formState.isSubmitting}
                >
                  Batal
                </Button>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  loading={formState.isSubmitting}
                >
                  Buat Faktur Pembelian
                </LoadingButton>
              </Stack>
            </Stack>
          </form>
        </FormProvider>
      </Stack>
    </PageContainer>
  );
};
